import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getTokens, refreshAccessToken } from '../api';

const SOCKET_URL = process.env.REACT_APP_API_URL || '';

export default function useSocket(handlers) {
  const socketRef = useRef(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const connect = useCallback(() => {
    const { access } = getTokens();
    if (!access) return;

    if (socketRef.current?.connected) return;

    const socket = io(SOCKET_URL, {
      auth: { token: access },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 20,
    });

    socket.on('connect', () => {
      console.log('Socket connected');
      handlersRef.current.onConnect?.(socket);
    });

    socket.on('message:new', (data) => {
      handlersRef.current.onMessage?.(data);
    });

    socket.on('message:read', (data) => {
      handlersRef.current.onMessageRead?.(data);
    });

    socket.on('message:typing', (data) => {
      handlersRef.current.onTyping?.(data);
    });

    socket.on('message:deleted', (data) => {
      handlersRef.current.onMessageDeleted?.(data);
    });

    socket.on('message:edited', (data) => {
      handlersRef.current.onMessageEdited?.(data);
    });

    socket.on('user:online', (data) => {
      handlersRef.current.onUserOnline?.(data);
    });

    socket.on('user:offline', (data) => {
      handlersRef.current.onUserOffline?.(data);
    });

    socket.on('users:online', (data) => {
      handlersRef.current.onUsersOnline?.(data);
    });

    socket.on('connect_error', async (err) => {
      console.error('Socket error:', err.message);
      if (err.message === 'AUTH_EXPIRED') {
        try {
          const newToken = await refreshAccessToken();
          // Обновляем handshake auth и форсируем переподключение с новым токеном
          socket.auth = { token: newToken };
          socket.disconnect().connect();
        } catch (refreshErr) {
          // Refresh не удался (refresh_token истёк/невалидный) — только тогда logout
          console.error('Refresh failed on socket AUTH_EXPIRED:', refreshErr);
          handlersRef.current.onAuthExpired?.();
        }
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    socketRef.current = socket;
  }, []);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const sendMessage = useCallback((data, callback) => {
    socketRef.current?.emit('message:send', data, callback);
  }, []);

  const markRead = useCallback((data) => {
    socketRef.current?.emit('message:read', data);
  }, []);

  const sendTyping = useCallback((roomId) => {
    socketRef.current?.emit('message:typing', { room_id: roomId });
  }, []);

  const deleteMessage = useCallback((messageId, callback) => {
    socketRef.current?.emit('message:delete', { message_id: messageId }, callback);
  }, []);

  const editMessage = useCallback((messageId, body, callback) => {
    socketRef.current?.emit('message:edit', { message_id: messageId, body }, callback);
  }, []);

  const sync = useCallback((rooms, callback) => {
    socketRef.current?.emit('sync', { rooms }, callback);
  }, []);

  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return {
    connect, disconnect, sendMessage, markRead,
    sendTyping, deleteMessage, editMessage, sync, socketRef,
  };
}
